import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../../api/axios";
import { useActiveLocation } from "../activeLocation";
import { PlatformsView } from "./PlatformsView";
import PlatformModal, { type PlatformPending } from "./PlatformModal";
import { usePlatforms } from "./usePlatforms";
import { Platform, PlatformUnit } from "./types";

function errorMessage(error: unknown): string {
  const anyErr = error as { response?: { data?: { message?: string } } };
  return anyErr?.response?.data?.message ?? "Something went wrong. Please try again.";
}

interface PlatformsOptions {
  managementBase: string;
  maintenance?: boolean;
}

export function createPlatforms({ managementBase, maintenance = false }: PlatformsOptions) {
  return function Platforms() {
    const { locationId, name } = useActiveLocation();
    const query = usePlatforms(locationId);
    const queryClient = useQueryClient();

    const [selectedPlatformId, setSelectedPlatformId] = useState<number | null>(null);
    const [errorText, setErrorText] = useState<string | null>(null);

    // Always read the freshest platform off the polled query so the modal's
    // status + stepper advance as data refetches.
    const selectedPlatform = selectedPlatformId != null
      ? query.data?.platforms.find((p) => p.id === selectedPlatformId) ?? null
      : null;

    const invalidate = () =>
      queryClient.invalidateQueries({ queryKey: ["platforms", locationId] });

    const onError = (error: unknown) => setErrorText(errorMessage(error));

    const requestMutation = useMutation({
      mutationFn: (unitId: number) => api.put(`/${managementBase}/retrieve-unit/${unitId}`),
      onError,
      onSettled: invalidate,
    });

    const returnMutation = useMutation({
      mutationFn: (unitId: number) => api.put(`/${managementBase}/return-unit/${unitId}`),
      onError,
      onSettled: invalidate,
    });

    const resetMutation = useMutation({
      mutationFn: (platformId: number) => api.post(`/platforms/${platformId}/reset`, { locationId }),
      onError,
      onSettled: invalidate,
    });

    const moveToMaintenanceMutation = useMutation({
      mutationFn: (platformId: number) => api.put(`/${managementBase}/maintenance/${platformId}`),
      onError,
      onSettled: invalidate,
    });

    const returnFromMaintenanceMutation = useMutation({
      mutationFn: (platformId: number) =>
        api.put(`/${managementBase}/return-from-maintenance/${platformId}`),
      onError,
      onSettled: invalidate,
    });

    const pending: PlatformPending | null = requestMutation.isPending
      ? { action: "request", unitId: requestMutation.variables }
      : returnMutation.isPending
      ? { action: "return", unitId: returnMutation.variables }
      : resetMutation.isPending
      ? { action: "reset" }
      : moveToMaintenanceMutation.isPending
      ? { action: "moveToMaintenance" }
      : returnFromMaintenanceMutation.isPending
      ? { action: "returnFromMaintenance" }
      : null;

    const openPlatform = (platform: Platform) => {
      setErrorText(null);
      setSelectedPlatformId(platform.id);
    };

    const closeModal = () => {
      setSelectedPlatformId(null);
      setErrorText(null);
    };

    return (
      <>
        <PlatformsView
          data={query.data}
          isLoading={query.isLoading}
          isError={query.isError}
          error={query.error}
          locationLabel={name}
          onSelectPlatform={openPlatform}
        />

        {selectedPlatform && (
          <PlatformModal
            opened
            onClose={closeModal}
            platform={selectedPlatform}
            onRequest={(unit: PlatformUnit) => {
              setErrorText(null);
              requestMutation.mutate(unit.unitId);
            }}
            onReturn={(unit: PlatformUnit) => {
              setErrorText(null);
              returnMutation.mutate(unit.unitId);
            }}
            onReset={() => {
              setErrorText(null);
              resetMutation.mutate(selectedPlatform.id);
            }}
            onMoveToMaintenance={
              maintenance
                ? () => {
                    setErrorText(null);
                    moveToMaintenanceMutation.mutate(selectedPlatform.id);
                  }
                : undefined
            }
            onReturnFromMaintenance={
              maintenance
                ? () => {
                    setErrorText(null);
                    returnFromMaintenanceMutation.mutate(selectedPlatform.id);
                  }
                : undefined
            }
            pending={pending}
            errorText={errorText}
            onDismissError={() => setErrorText(null)}
          />
        )}
      </>
    );
  };
}
